import { useState } from 'react';

const Contact = ({ useH1 = false }) => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        serviceInterest: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', or null

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            // Use relative path for API calls - works in all environments
            const apiPath = '/api/contact';
            const res = await fetch(apiPath, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone || '',
                    subject: formData.serviceInterest || 'General Inquiry',
                    message: `
Service Interest: ${formData.serviceInterest || 'Not specified'}

Message:
${formData.message}
                    `.trim()
                })
            });

            if (!res.ok) throw new Error('Network response was not ok');

            setSubmitStatus('success');
            setFormData({ name: '', email: '', phone: '', serviceInterest: '', message: '' });
            
            // Track Meta Pixel event
            if (typeof window !== 'undefined' && window.fbq) {
                window.fbq('track', 'Contact', {
                    content_name: 'Contact Form Submission',
                    content_category: formData.serviceInterest || 'General Inquiry'
                });
            }
            
            // Auto-hide success message after 5 seconds
            setTimeout(() => setSubmitStatus(null), 5000);
        } catch (err) {
            console.error(err);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section id="contact" className="section contact">
            <div className="section-inner">
                {useH1 ? (
                    <h1 className="title title-h2">Get In<br />Touch</h1>
                ) : (
                    <h2 className="title title-h2">Get In<br />Touch</h2>
                )}
                <h3 className="subtitle subtitle-h3">Start Your Project</h3>
                <p className="description">
                    Ready to start your next project? Contact us today for a free consultation.
                </p>
                <div className="contact-container">
                    {submitStatus === 'success' && (
                        <div className="form-message success-message">
                            <i className="fas fa-check-circle"></i>
                            <p><strong>Thank you!</strong> Your message has been sent successfully. We'll get back to you soon.</p>
                        </div>
                    )}
                    {submitStatus === 'error' && (
                        <div className="form-message error-message">
                            <i className="fas fa-exclamation-circle"></i>
                            <p><strong>Oops!</strong> There was an error sending your message. Please try again.</p>
                        </div>
                    )}
                    <form id="contactForm" className="contact-form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="name">Your Name *</label>
                            <input 
                                id="name"
                                type="text" 
                                name="name" 
                                placeholder="Your Name *" 
                                required
                                value={formData.name}
                                onChange={handleChange}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="email">Your Email *</label>
                            <input 
                                id="email"
                                type="email" 
                                name="email" 
                                placeholder="Your Email *" 
                                required
                                value={formData.email}
                                onChange={handleChange}
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="phone">Phone Number</label>
                            <input
                                id="phone"
                                type="text"
                                name="phone"
                                placeholder="Phone Number"
                                value={formData.phone || ''}
                                onChange={handleChange}
                                disabled={isSubmitting}
                            />
                        </div>
                        
                        {/* Service Interest Dropdown */}
                        <div className="form-group">
                            <label htmlFor="serviceInterest">What service are you interested in? *</label>
                            <select
                                id="serviceInterest"
                                name="serviceInterest"
                                required
                                value={formData.serviceInterest}
                                onChange={handleChange}
                                disabled={isSubmitting}
                            >
                                <option value="">-- Select a Service --</option>
                                <option value="Web Development">Web Development</option>
                                <option value="WordPress Website">WordPress Website</option>
                                <option value="E-commerce / WooCommerce">E-commerce / WooCommerce</option>
                                <option value="Custom Web Application">Custom Web Application</option>
                                <option value="React/Next.js Application">React/Next.js Application</option>
                                <option value="Private Cloud Setup">Private Cloud Setup (Nextcloud)</option>
                                <option value="Full Cloud Suite (Nextcloud + CRM + n8n)">Full Cloud Suite (Nextcloud + CRM + n8n)</option>
                                <option value="Digital Marketing">Digital Marketing</option>
                                <option value="Social Media Management">Social Media Management</option>
                                <option value="Google Ads">Google Ads</option>
                                <option value="Meta Ads (Facebook/Instagram)">Meta Ads (Facebook/Instagram)</option>
                                <option value="SEO Optimization">SEO Optimization</option>
                                <option value="Technical SEO Audit">Technical SEO Audit</option>
                                <option value="Monthly SEO Service">Monthly SEO Service</option>
                                <option value="n8n Workflow Automation">n8n Workflow Automation</option>
                                <option value="Social Media Account Setup">Social Media Account Setup & Integration</option>
                                <option value="Website Maintenance">Website Maintenance</option>
                                <option value="Multiple Services / Package">Multiple Services / Package Deal</option>
                                <option value="Other / General Inquiry">Other / General Inquiry</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label htmlFor="message">Tell us more about your project *</label>
                            <textarea 
                                id="message"
                                name="message" 
                                rows="5" 
                                placeholder="Describe your project, goals, or any specific requirements..." 
                                required
                                value={formData.message}
                                onChange={handleChange}
                                disabled={isSubmitting}
                            ></textarea>
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                            {isSubmitting ? 'Sending...' : 'Send Message'}
                        </button>
                    </form>
                    <div className="contact-info">
                        <h3>Contact Information</h3>
                        <p><i className="fas fa-map-marker-alt"></i> Centurion, South Africa</p>
                        <p><i className="fas fa-phone"></i> +27 84 639 0621</p>
                        <p><i className="fas fa-envelope"></i> info@rdsstudio.co.za</p>
                        <p><i className="fas fa-clock"></i> Mon-Fri: 9AM-6PM</p>
                        <div className="social-links">
                            <a href="https://www.facebook.com/profile.php?id=61579996723546" target="_blank" rel="noopener noreferrer" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
                            <a href="https://www.instagram.com/rdsstudio1111/" target="_blank" rel="noopener noreferrer" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
                            <a href="https://www.linkedin.com/in/jean-pierre-matthys-du-toit-7671493a3/?originalSubdomain=za" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn"><i className="fab fa-linkedin-in"></i></a>
                            <a href="https://www.youtube.com/channel/UCqEgC_kMwk4MqEcIC_yCayw" target="_blank" rel="noopener noreferrer" aria-label="YouTube"><i className="fab fa-youtube"></i></a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Contact;